import React, { useMemo } from "react";
import { Controller } from "react-hook-form";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import useKenyaFacilities from "hooks/useKenyaFacilities";

/**
 * FacilitySearch - picker for the curated Kenyan facility list.
 *
 * Replaces the Algolia-backed EmergencySearch: the whole Kenyan list is loaded
 * once by useKenyaFacilities and filtered here in memory, so typing costs no
 * network calls and no Firestore reads. Selection shape stays
 * `{ id, title, county, ... }` and the field name stays `emergency_location`,
 * so AccountSetupFlow's claim flow (locations doc id -> requestFacilityClaim)
 * is unchanged.
 */

const normalize = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/['’.,()/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

// Rank: whole-query prefix on the name beats a word-start match, which beats a
// loose substring hit. Curated priority tier and name break remaining ties.
const scoreMatch = (facility, query) => {
  const name = normalize(facility.title);
  const haystack = `${name} ${normalize(facility.county)}`;
  const tokens = query.split(" ").filter(Boolean);

  if (!tokens.every((token) => haystack.includes(token))) return null;

  if (name.startsWith(query)) return 0;
  if (new RegExp(`\\b${tokens[0]}`).test(name)) return 1;
  if (name.includes(query)) return 2;
  return 3;
};

const MAX_RESULTS = 50;

// One line per option, matching the shape of the old Algolia dropdown
// ("name, city, state zip"): "Kenyatta National Hospital, Nairobi, Kenya".
const facilityLabel = (facility) => {
  if (!facility?.title) return "";
  const place = [facility.county, "Kenya"].filter(Boolean).join(", ");
  return [`${facility.title}, ${place}`, facility.zip]
    .filter(Boolean)
    .join(" ");
};

const FacilitySearch = ({ control, errors }) => {
  const { facilities, loading, error } = useKenyaFacilities();

  const filterOptions = useMemo(
    () => (options, { inputValue }) => {
      const query = normalize(inputValue);
      if (!query) return options.slice(0, MAX_RESULTS);

      return options
        .map((facility) => ({ facility, score: scoreMatch(facility, query) }))
        .filter((entry) => entry.score !== null)
        .sort(
          (a, b) =>
            a.score - b.score ||
            a.facility.priorityTier - b.facility.priorityTier ||
            a.facility.title.localeCompare(b.facility.title)
        )
        .slice(0, MAX_RESULTS)
        .map((entry) => entry.facility);
    },
    []
  );

  const helperText =
    errors?.emergency_location?.message ||
    (error
      ? "We could not load the facility list. Please check your connection and try again."
      : "If your facility is not listed, please contact us at support@myhealthdesk.com.");

  return (
    <Controller
      control={control}
      name="emergency_location"
      rules={{
        validate: (value) =>
          (value && typeof value === "object" && !!value.id) ||
          "Please select your facility from the list.",
      }}
      render={({ field: { onChange, value } }) => (
        <Autocomplete
          id="emergency_location"
          options={facilities}
          filterOptions={filterOptions}
          onChange={(event, item) => onChange(item)}
          value={value || null}
          loading={loading}
          disabled={loading && !facilities.length}
          noOptionsText="No matching facility"
          getOptionLabel={facilityLabel}
          isOptionEqualToValue={(option, selected) =>
            option?.id === selected?.id
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Facility"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="primary" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              helperText={helperText}
              error={!!errors?.emergency_location || !!error}
            />
          )}
        />
      )}
    />
  );
};

export default FacilitySearch;
